import React from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, Users, Brain, Swords, Users2, Share2, GitCompare,
  LayoutTemplate, DollarSign, KeyRound, Heart, ShieldCheck,
  ArrowRight, Gavel, Cpu, Layers
} from "lucide-react";
import Logo from "../components/Logo";
import { BrainMarquee, BrainConstellation } from "../components/BrainConstellation";

/* -------------------------- Content -------------------------- */

const PRINCIPLES = [
  {
    icon: Heart,
    title: "Persona outlives vendor.",
    body: "Every LLM release resets your setup elsewhere. Not here. Your worker keeps their name, mission, memory, and history no matter which brain you swap in.",
    color: "#EF4444",
  },
  {
    icon: Layers,
    title: "Multi-brain by default.",
    body: "12 models across 8 providers behind one unified interface. Bring your own OpenAI / Anthropic / Google keys to bypass the middleman and cut cost 30-70%.",
    color: "#3B82F6",
  },
  {
    icon: Swords,
    title: "The debate is the demo.",
    body: "Give the same prompt to 5 workers with different stances. A judge weighs the arguments in a live streamed verdict. Every debate is shareable in one click.",
    color: "#F59E0B",
  },
  {
    icon: ShieldCheck,
    title: "Your workforce, your keys.",
    body: "Workers, conversations, debates, and API keys are scoped to your account. Direct provider keys are encrypted at rest. No training on your prompts. No ads.",
    color: "#10B981",
  },
];

const FEATURES = [
  { icon: Users,          title: "Persistent workers",  body: "Create AI colleagues with names, missions, memories, and expertise. Chat with each independently.",             color: "#3B82F6" },
  { icon: Brain,          title: "Brain swap",           body: "Change which LLM powers a worker anytime. Persona, memory, and history are preserved.",                        color: "#10B981" },
  { icon: Swords,         title: "Debate mode",          body: "2–5 workers argue their stances. Judge worker delivers a structured verdict. Live SSE streaming.",             color: "#EF4444" },
  { icon: Users2,         title: "Teams",                body: "Group workers under a leader with shared instructions and memory. Broadcast prompts to the whole team.",       color: "#8B5CF6" },
  { icon: LayoutTemplate, title: "Templates gallery",    body: "Save any worker as a template. Clone the community's best setups in one click. Publish yours.",               color: "#EC4899" },
  { icon: Share2,         title: "Public share links",   body: "Templates, conversations, and debates become no-auth URLs. Snapshot-based — no live LLM leak.",                color: "#D97706" },
  { icon: GitCompare,     title: "Compare mode",         body: "Fan the same prompt out to up to 4 brains simultaneously and watch them stream side-by-side.",                 color: "#0EA5E9" },
  { icon: KeyRound,       title: "Direct provider keys", body: "Store your OpenAI/Anthropic/Google keys once (encrypted). Every worker on those brains bypasses OpenRouter.", color: "#6366F1" },
  { icon: DollarSign,     title: "Cost budgets",         body: "Daily and monthly caps per worker or globally. Live spend counter. Warn at 80%, block at 100%.",              color: "#059669" },
];

/* ---------------------------- Page ---------------------------- */

export default function About() {
  return (
    <div className="min-h-screen bg-[#FDF8F5]">
      {/* Top bar */}
      <header className="px-6 py-5 flex items-center justify-between max-w-6xl mx-auto">
        <Link to="/" data-testid="brand-link">
          <Logo size={36} />
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/login" className="text-sm font-bold text-slate-600 hover:text-slate-900" data-testid="login-link">
            Sign in
          </Link>
          <Link to="/login" className="brut-btn text-sm" data-testid="cta-top">
            Get started <ArrowRight size={14} strokeWidth={2.5} />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 pt-10 pb-10 max-w-5xl mx-auto" data-testid="about-hero">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-200 border-2 border-slate-900 font-heading font-semibold text-xs uppercase tracking-widest mb-6">
          <span className="w-2 h-2 rounded-full bg-emerald-500 dot-blink" /> About AiMedley
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
          Build a team of AI experts.<br />
          <span className="bg-amber-200 px-2 -rotate-1 inline-block border-2 border-slate-900 rounded-lg">Choose</span> their brains.
        </h1>
        <p className="text-lg md:text-xl text-slate-600 mt-6 max-w-2xl font-medium leading-relaxed">
          AiMedley is where <strong>personas outlive vendors</strong>. Create persistent AI workers — give each a name, mission and personality — then pick which of <strong>12 brains across 8 providers</strong> thinks for them today. Swap models tomorrow without losing anything.
        </p>

        <div className="mt-8 grid sm:grid-cols-3 gap-3 max-w-2xl">
          <StatBadge label="Brains" value="12" />
          <StatBadge label="Providers" value="8" />
          <StatBadge label="Lock-in" value="0" />
        </div>
      </section>

      {/* Brain marquee under hero */}
      <div className="mb-16" data-testid="brain-strip">
        <BrainMarquee speed={45} />
      </div>

      {/* Debate spotlight — the killer feature */}
      <section className="px-6 pb-16 max-w-6xl mx-auto" data-testid="about-debate">
        <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-3">The flagship</div>
        <div className="grid md:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-8 items-center">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Swords size={22} strokeWidth={2.5} />
              <span className="text-xs uppercase tracking-widest font-bold text-slate-500">Debate mode</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold leading-tight mb-4">
              Put your workers in the ring.
            </h2>
            <p className="text-slate-600 text-base leading-relaxed mb-4">
              Same prompt. 2–5 workers. Each argues a different stance. A judge worker weighs the arguments and delivers a structured verdict — live, in parallel, in under a minute.
            </p>
            <ul className="space-y-2 text-slate-700 text-sm">
              {[
                "Parallel SSE streaming — see all sides at once",
                "Structured verdict: winner, why, where each side fell short",
                "Every debate auto-saved & one-click shareable",
                "Typical cost: ~$0.01 per debate",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-emerald-600 font-bold mt-0.5">✓</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <DebateMockup />
        </div>
      </section>

      {/* Principles */}
      <section className="px-6 pb-16 max-w-6xl mx-auto" data-testid="about-principles">
        <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-3">Why we built it</div>
        <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-10 max-w-2xl">
          Four beliefs behind AiMedley.
        </h2>
        <div className="grid md:grid-cols-2 gap-5">
          {PRINCIPLES.map(({ icon: I, title, body, color }, i) => (
            <div key={i} className="brut-card p-6" data-testid={`principle-${i}`}>
              <div
                className="w-11 h-11 rounded-xl border-2 flex items-center justify-center mb-3"
                style={{ backgroundColor: color + "22", borderColor: color, color }}
              >
                <I size={20} strokeWidth={2.5} />
              </div>
              <h3 className="font-heading text-xl font-bold mb-2">{title}</h3>
              <p className="text-slate-600 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* The Brain Catalog */}
      <section className="px-6 pb-16 max-w-6xl mx-auto" data-testid="about-brains">
        <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-3">The catalog</div>
        <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3 max-w-2xl">
          Twelve brains. Eight providers. One interface.
        </h2>
        <p className="text-slate-600 max-w-2xl mb-8">
          Every brain behaves the same way to your workers — swap them like keyboard shortcuts. Bring your own keys where you can to cut cost and latency.
        </p>
        <BrainConstellation />
      </section>

      {/* Features grid */}
      <section className="px-6 pb-16 max-w-6xl mx-auto" data-testid="about-features">
        <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-3">What's shipped</div>
        <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-10 max-w-2xl">
          Nine tools. Zero lock-in.
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map(({ icon: I, title, body, color }, i) => (
            <div key={i} className="brut-card p-5" data-testid={`feature-${i}`}>
              <div
                className="w-10 h-10 rounded-lg border-2 flex items-center justify-center mb-3"
                style={{ backgroundColor: color + "1F", borderColor: color, color }}
              >
                <I size={18} strokeWidth={2.5} />
              </div>
              <h3 className="font-heading text-lg font-bold mb-1.5">{title}</h3>
              <p className="text-sm text-slate-600 leading-snug">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="px-6 pb-16 max-w-5xl mx-auto" data-testid="about-howitworks">
        <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-3">Under the hood</div>
        <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-8 max-w-2xl">
          How it works.
        </h2>
        <div className="brut-card p-7 space-y-5">
          {[
            ["1", "Register → workforce seeded in 30 seconds.",
             "The moment you sign up we spin up 4 demo workers + 1 team so you can run a debate immediately. No setup, no config, no prompt engineering."],
            ["2", "One prompt, many brains.",
             "Behind every worker is a unified `chat_stream(brain_id, msgs)` interface. Switch a worker from GPT-5.1 to Claude Sonnet 4.6 to Gemini 2.5 Pro with a dropdown."],
            ["3", "Bring your own keys (optional).",
             "Save your OpenAI / Anthropic / Google keys once. Workers on those brains bypass OpenRouter and use the vendor natively — cheaper, faster, no middleman margin."],
            ["4", "Debates run in parallel, judged sequentially.",
             "Each round fans debaters out in parallel via server-side asyncio queues. Once every debater finishes, the next round starts. The judge sees the full transcript and streams a structured verdict."],
            ["5", "Share = snapshot, not live query.",
             "When you share a template, chat, or debate we freeze the payload. Public viewers never re-trigger LLM calls or leak live worker state. You can safely edit the source after."],
          ].map(([n, title, body]) => (
            <div key={n} className="flex gap-4">
              <div className="w-10 h-10 shrink-0 rounded-xl bg-slate-900 text-white font-heading font-bold text-lg flex items-center justify-center border-2 border-slate-900">
                {n}
              </div>
              <div>
                <div className="font-heading text-lg font-bold">{title}</div>
                <p className="text-slate-600 leading-relaxed">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Stack */}
      <section className="px-6 pb-16 max-w-5xl mx-auto" data-testid="about-stack">
        <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-3">Stack</div>
        <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-8 max-w-2xl">
          Boring, dependable pieces.
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { name: "FastAPI",    desc: "Async Python backend" },
            { name: "MongoDB",    desc: "Workforce state" },
            { name: "OpenRouter", desc: "Default brain gateway" },
            { name: "Direct SDKs",desc: "OpenAI · Anthropic · Google" },
            { name: "React 19",   desc: "Frontend" },
            { name: "Tailwind",   desc: "Styling" },
            { name: "shadcn/ui",  desc: "Primitives" },
            { name: "SSE",        desc: "Realtime streaming" },
          ].map(({ name, desc }) => (
            <div key={name} className="border-2 border-slate-900 rounded-xl bg-white p-3">
              <div className="font-heading font-bold">{name}</div>
              <div className="text-xs text-slate-500 font-medium">{desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 pb-20 max-w-4xl mx-auto" data-testid="about-cta">
        <div className="brut-card p-8 sm:p-12 text-center relative overflow-hidden">
          <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-amber-200 opacity-40 float-y" />
          <div className="absolute -bottom-10 left-10 w-32 h-32 rounded-full bg-emerald-200 opacity-40 float-y" style={{ animationDelay: "1.5s" }} />
          <div className="relative">
            <Sparkles className="mx-auto mb-3 text-slate-900" size={36} strokeWidth={2.5} />
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-2">Ready to hire?</h2>
            <p className="text-slate-600 mb-6 max-w-md mx-auto">
              Spin up a 4-worker team in 30 seconds. Run your first debate for a penny. No card, no lock-in.
            </p>
            <Link to="/login" className="brut-btn" data-testid="cta-bottom">
              Create my workforce <ArrowRight size={16} strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      </section>

      <footer className="px-6 pb-10 text-center text-xs text-slate-400 font-semibold">
        AiMedley · Where personas outlive vendors · {new Date().getFullYear()}
      </footer>
    </div>
  );
}

/* -------------------------- bits -------------------------- */

function StatBadge({ label, value }) {
  return (
    <div className="brut-card p-4 bg-white">
      <div className="font-heading font-bold text-3xl leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mt-1">{label}</div>
    </div>
  );
}

function DebateMockup() {
  return (
    <div className="brut-card p-4 bg-white rotate-[0.4deg]" data-testid="debate-mockup">
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">Topic</div>
      <div className="font-heading font-bold text-lg mb-3 leading-tight">
        Should companies mandate quarterly in-person offsites?
      </div>

      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">Round 1 · Live</div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <MiniTurn avatar="👩‍💼" name="Nina" stance="For" color="#10B981"
                  text="Async slays daily standups, but relationships get built in the messy edges…" />
        <MiniTurn avatar="🧑‍💻" name="Rio" stance="Against" color="#EF4444"
                  text="Every mandated offsite is a productivity tax on introverts who ship best solo…" />
      </div>

      <div className="brut-card p-3 bg-amber-50">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">
          <Gavel size={11} strokeWidth={3} /> Verdict by Judge Marla
        </div>
        <div className="text-sm text-slate-800">
          <b>Winner: Nina (narrow).</b> Rio's productivity math holds, but Nina's "trust IOU" model ships better long-run.
        </div>
      </div>

      <div className="flex items-center gap-2 mt-3 text-[10px] font-mono text-slate-400">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 dot-blink" />
        Live SSE · $0.008 · 41s
      </div>
    </div>
  );
}

function MiniTurn({ avatar, name, stance, color, text }) {
  return (
    <div className="brut-card p-2.5 bg-white">
      <div className="flex items-center gap-1.5 mb-1 text-xs">
        <span>{avatar}</span>
        <span className="font-heading font-bold">{name}</span>
        <span
          className="px-1.5 py-0.5 rounded-full border text-[9px] font-bold uppercase tracking-widest"
          style={{ borderColor: color, color, backgroundColor: color + "1F" }}
        >
          {stance}
        </span>
      </div>
      <div className="text-[11px] text-slate-600 leading-snug">{text}</div>
    </div>
  );
}
